export function findNodeByKey(
  data: OrgTreeItem[],
  key: string
): OrgTreeItem | null {
  let stack = [...data];
  while (stack.length) {
    let node = stack.shift()!;
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      stack.push(...node.children);
    }
  }
  return null;
}

export function findNodeById(
  orgTreeData: OrgTreeItem[],
  id: string
): OrgTreeItem | null {
  let stack = [...orgTreeData];
  while (stack.length) {
    let node = stack.shift();
    if (node && node.teamId === id) {
      return node;
    }
    if (node && node.children) {
      stack.push(...node.children);
    }
  }
  return null;
}

export function findNodeByLabel(
  orgTreeData: OrgTreeItem[],
  label: string
): OrgTreeItem | null {
  let stack = [...orgTreeData];
  function labelTypoCheck(label: string) {
    switch (label) {
      case "Sunshine Coast South":
        return "Sunshine Coast - South";
      default:
        return label;
    }
  }
  const labelCheckedForTypos = labelTypoCheck(label);
  while (stack.length) {
    let node = stack.shift()!;
    if (
      node.label
        .toLowerCase()
        .trim()
        .includes(labelCheckedForTypos.toLowerCase().trim()) ||
      labelCheckedForTypos
        .toLowerCase()
        .trim()
        .includes(node.label.toLowerCase().trim())
    ) {
      return node;
    }
    if (node.children) {
      stack.push(...node.children);
    }
  }
  return null;
}

export function parentKeyFromChildKey(id: string) {
  const lastIndex = id.lastIndexOf("-");
  if (lastIndex !== -1) {
    return id.slice(0, lastIndex);
  }
  return id;
}

export function findParentNode(
  orgTreeDatabase: OrgTreeItem,
  targetNode: OrgTreeItem
): OrgTreeItem | null {
  let stack = [orgTreeDatabase];
  while (stack.length) {
    let node = stack.shift();
    if (!node) continue;
    const nodeChildrenHasNodeId = node?.children?.some(
      (child) => child.teamId === targetNode.teamId
    );
    if (nodeChildrenHasNodeId) {
      return node;
    }
    if (node && node.children) {
      stack.push(...node.children);
    }
  }
  return null;
}

// Externalized function to find the path to a node
/* const findPathToNode = (
  currentNode: OrgTreeItem,
  targetNode: OrgTreeItem,
  path: (number | "children")[] = []
): (number | "children")[] | null => {
  if (currentNode.teamId === targetNode.teamId) {
    return path;
  }
  if (currentNode.children) {
    for (let i = 0; i < currentNode.children.length; i++) {
      const newPath = findPathToNode(currentNode.children[i], targetNode, [
        ...path,
        "children",
        i,
      ]);
      if (newPath) {
        return newPath;
      }
    }
  }
  return null;
}; */

export const moveNodeInTree = (
  OrgTree: OrgTreeItem,
  targetNode: OrgTreeItem,
  newParentNode: OrgTreeItem
): OrgTreeItem => {
  const treeCopy = JSON.parse(JSON.stringify(OrgTree));

  const parentNode = findParentNode(treeCopy, targetNode);

  // Remove the targetNode from its current position
  if (parentNode && parentNode.children) {
    const indexOfTarget = parentNode.children.findIndex(
      (child) => child.teamId === targetNode.teamId
    );
    if (indexOfTarget !== -1) {
      parentNode.children.splice(indexOfTarget, 1);
      parentNode.children.forEach((child, index) => {
        child.key = `${parentNode.key}-${index}`;
      });
    }
  }

  // Add the targetNode to the newParentNode
  const newParent = findNodeById([treeCopy], newParentNode.teamId);
  if (newParent) {
    if (!newParent.children) {
      newParent.children = [];
    }
    newParent.children.forEach((child, index) => {
      child.key = `${newParent.key}-${index}`;
    });
    targetNode.key = `${newParent.key}-${newParent.children.length}`;
    newParent.children.push(targetNode);
  }

  return treeCopy;
};

function getLeafkeys(node: OrgTreeItem) {
  if (!node.children || node.children.length === 0) {
    // The node is a leaf
    return [node.key];
  } else {
    // The node has children. Recursively get the labels of the leaves.
    let labels: string[] = [];
    node.children.forEach((child) => {
      labels.push(...getLeafkeys(child));
    });
    return labels;
  }
}

function getUserLeafKeys(data: OrgTreeItem[], key: string) {
  let node = findNodeByKey(data, key);
  if (node !== null) {
    return getLeafkeys(node);
  }
  return [];
}

function sortChildrenByLabel(node: OrgTreeItem): OrgTreeItem {
  // If the node has children, sort them
  if (node.children && node.children.length > 0) {
    node.children.sort((a, b) => a.label.localeCompare(b.label));

    // Recursively sort the children of each child node
    node.children.forEach((child) => sortChildrenByLabel(child));
  }

  return node;
}

const addUserToCampus = (node: OrgTreeItem, user: UserData) => {
  if (
    user.PCRoles.some(
      (role) =>
        ["volunteer", "churchStaff", "pastoralCare"].includes(role.role) &&
        role.teamId === node.teamId
    )
  ) {
    if (!node.children) {
      node.children = [];
    }

    const newUserKey = `${node.key}-${node.children.length}`;
    node.children.push({
      key: newUserKey,
      label: `${user.firstName} ${user.lastName}`,
      teamId: user.userId,
      teamType: "volunteer",
      visible: true,
    });
    return;
  }
  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      addUserToCampus(node.children[i], user);
    }
  }
};

export const generateCustomDashboardSelectorOrgTreeData = (
  orgTreeDatabase: OrgTreeItem,
  usersDatabase: UserData[]
): OrgTreeItem[] => {
  const users = usersDatabase;
  const orgTree = [orgTreeDatabase];

  const fliteredOrgTree: OrgTreeItem[] = [];

  const FilterOrgTree = (node: OrgTreeItem): OrgTreeItem | null => {
    if (node.visible !== true) {
      return null;
    }

    let newNode: OrgTreeItem = { ...node };

    if (newNode.children) {
      newNode.children = newNode.children
        .map((child) => FilterOrgTree(child))
        .filter(Boolean) as OrgTreeItem[];
    }

    return newNode;
  };

  if (orgTree) {
    for (let i = 0; i < orgTree.length; i++) {
      const filteredNode = FilterOrgTree(orgTree[i]);
      if (filteredNode) {
        fliteredOrgTree.push(filteredNode);
      }
    }
  }

  users.forEach((user) => {
    addUserToCampus(fliteredOrgTree[0], user);
  });

  sortChildrenByLabel(fliteredOrgTree[0]);

  return fliteredOrgTree;
};

export function prepareOrgChartTree(orgTreeDatabase: OrgTreeItem): OrgTreeItem {
  // Recursive function to set expanded property
  function setExpandedProperty(
    node: OrgTreeItem,
    isRoot: boolean = false
  ): OrgTreeItem {
    return {
      ...node,
      expanded: isRoot,
      selectable: true,
      data: {
        name: node.label,
        title: node.label,
      },
      children: node.children
        ? node.children.map((child) => setExpandedProperty(child))
        : undefined,
    };
  }

  const fliteredOrgTree = setExpandedProperty(orgTreeDatabase, true);

  sortChildrenByLabel(fliteredOrgTree);

  return sortChildrenByLabel(fliteredOrgTree);
}

//add the property bucketIds to every node of the orgTree and make it an array with the campusId as the first element
export function addBucketIdsToOrgTree(orgTreeDatabase: OrgTreeItem) {
  // Recursive function to set expanded property
  function setBucketIds(node: OrgTreeItem): OrgTreeItem {
    return {
      ...node,
      databaseBuckets: [node.teamId],
      children: node.children
        ? node.children.map((child) => setBucketIds(child))
        : undefined,
    };
  }

  console.log(setBucketIds(orgTreeDatabase));
}

export function compareDepthLevelOfNodes(
  teamIdNode1: OrgTreeItem,
  teamIdNode2: OrgTreeItem
): number {
  const teamIdNode1Array = teamIdNode1.key.split("-");
  const teamIdNode2Array = teamIdNode2.key.split("-");

  if (teamIdNode1Array.length > teamIdNode2Array.length) {
    return 1;
  } else if (teamIdNode1Array.length < teamIdNode2Array.length) {
    return -1;
  } else {
    return 0;
  }
}

// Function to find a node by its teamId
function findNodeByTeamId(
  root: OrgTreeItem,
  teamId: string
): OrgTreeItem | null {
  if (root.teamId === teamId) return root;
  if (root.children) {
    for (let child of root.children) {
      const result = findNodeByTeamId(child, teamId);
      if (result) return result;
    }
  }
  return null;
}

function removeNonVisibleNodes(orgTree: OrgTreeItem[]): OrgTreeItem[] {
  return orgTree
    .filter((node) => node.visible !== false)
    .map((node) => {
      if (node.children) {
        node.children = removeNonVisibleNodes(node.children);
      }
      return node;
    });
}

function removeNonReportingPeopleInOrgTree(
  orgTree: OrgTreeItem[]
): OrgTreeItem[] {
  const newOrgTree = [...orgTree];
  const removeAdminPeople = (node: OrgTreeItem) => {
    if (node.children) {
      node.children = node.children.filter(
        (child) =>
          child.teamType !== "admin" &&
          child.teamType !== "campusPastor" &&
          child.teamType !== "oversight"
      );
      node.children = node.children.filter(
        (child, index, self) =>
          index === self.findIndex((t) => t.teamId === child.teamId)
      );
      node.children.forEach((child) => {
        removeAdminPeople(child);
      });
    }
  };
  newOrgTree.forEach((node) => {
    removeAdminPeople(node);
  });
  return newOrgTree;
}
//takes in a User object and from it's roles array, find for every role the corresponding node in the orgTree and store it to an array
export function createNodesWithUsersFromUserInfo(
  user: UserData,
  orgTree: OrgTreeItem
): OrgTreeItem[] | undefined {
  const nodes: OrgTreeItem[] = [];
  if (user.PCRoles) {
    user.PCRoles.forEach((role) => {
      const correspondingNode = JSON.parse(
        JSON.stringify(findNodeByTeamId(orgTree, role.teamId))
      );
      if (!correspondingNode) return;

      if (
        role.role === "volunteer" ||
        role.role === "churchStaff" ||
        role.role === "pastoralCare"
      ) {
        correspondingNode.children = [];
        correspondingNode.selectable = false;
        const newUserKey = `${correspondingNode.key}-${correspondingNode.children.length}`;
        correspondingNode.children.push({
          key: newUserKey,
          label: `${user.firstName} ${user.lastName}`,
          teamId: user.userId,
          teamType: "individual",
          visible: true,
        });
      }
      nodes.push(correspondingNode);
    });
    const filteredOrgTreeArray = filterOutDependentNodes(nodes);
    const switchSelectable = removeNonVisibleNodes(filteredOrgTreeArray);
    const noAdmins = removeNonReportingPeopleInOrgTree(switchSelectable);
    //iterates through all the nodes in the tree and organise them in alphabetical order of their label property
    noAdmins.forEach((node) => {
      sortChildrenByLabel(node);
    });
    return noAdmins as OrgTreeItem[];
  }
}

export function createNodesHighestAccessAndPersonalNode(
  user: UserData,
  orgTree: OrgTreeItem
): OrgTreeItem[] | undefined {
  const nodes: OrgTreeItem[] = [];
  if (user.PCRoles) {
    user.PCRoles.forEach((role) => {
      const correspondingNode = JSON.parse(
        JSON.stringify(findNodeByTeamId(orgTree, role.teamId))
      );
      if (!correspondingNode) return;

      if (
        role.role === "volunteer" ||
        role.role === "churchStaff" ||
        role.role === "pastoralCare"
      ) {
        correspondingNode.children = [];
        correspondingNode.selectable = false;
        const newUserKey = `${correspondingNode.key}-${correspondingNode.children.length}`;
        correspondingNode.children.push({
          key: newUserKey,
          label: `${user.firstName} ${user.lastName}`,
          teamId: user.userId,
          teamType: "individual",
          visible: true,
        });
      }
      nodes.push(correspondingNode);
    });
    const filteredOrgTreeArray = filterOutDependentNodes(nodes);
    const switchSelectable = removeNonVisibleNodes(filteredOrgTreeArray);
    const noUsers = removeAllUsersExceptCurrentUserFromTree(
      switchSelectable,
      user
    );
    //iterates through all the nodes in the tree and organise them in alphabetical order of their label property
    noUsers.forEach((node) => {
      sortChildrenByLabel(node);
    });
    return noUsers as OrgTreeItem[];
  }
}

function removeAllUsersExceptCurrentUserFromTree(
  orgTree: OrgTreeItem[],
  user: UserData
) {
  const newOrgTree = [...orgTree];
  const removeUsers = (node: OrgTreeItem) => {
    if (node.children) {
      node.children = node.children.filter(
        (child) =>
          child.teamType !== "volunteer" &&
          child.teamType !== "churchStaff" &&
          child.teamType !== "pastoralCare" &&
          child.teamType !== "oversight" &&
          child.teamType !== "campusPastor" &&
          child.teamType !== "admin"
      );
      node.children = node.children.filter(
        (child, index, self) =>
          index === self.findIndex((t) => t.teamId === child.teamId)
      );
      node.children.forEach((child) => {
        removeUsers(child);
      });
    }
  };
  newOrgTree.forEach((node) => {
    removeUsers(node);
  });
  const userKey = `0-${newOrgTree.length}`
  //if user has a reporting role add the user to the orgTree
  if (user.PCRoles?.some((role) => role.role === "volunteer" || role.role === "churchStaff" || role.role === "pastoralCare")) {
    newOrgTree.push({
      key:userKey,
      label: `${user.firstName} ${user.lastName}`,
      teamId: user.userId,
      teamType: "churchStaff",
      visible: true,
    })
  }
  return newOrgTree;
}

export const createNodesFromUserRoles = (
  user: UserData,
  orgTree: OrgTreeItem
) => {
  const nodes: OrgTreeItem[] = [];
  if (user.PCRoles) {
    user.PCRoles.forEach((role) => {
      const correspondingNode = JSON.parse(
        JSON.stringify(findNodeByTeamId(orgTree, role.teamId))
      );
      nodes.push(correspondingNode);
    });
  }
  const filteredOrgTreeArray = filterOutDependentNodes(nodes);
  return filteredOrgTreeArray as OrgTreeItem[];
};

//remove individual nodes from orgTree
export function removeIndividualNodes(orgTree: OrgTreeItem[]) {
  const newOrgTree = [...orgTree];
  const removeIndividualNodesFromNode = (node: OrgTreeItem) => {
    if (
      node.teamType === "volunteer" ||
      node.teamType === "churchStaff" ||
      node.teamType === "pastoralCare" ||
      node.teamType === "oversight" ||
      node.teamType === "campusPastor" ||
      node.teamType === "admin"
    ) {
      return null;
    }
    if (node.children) {
      node.children = node.children
        .map((child) => removeIndividualNodesFromNode(child))
        .filter(Boolean) as OrgTreeItem[];
    }
    return node;
  };
  newOrgTree.forEach((node: OrgTreeItem) => {
    removeIndividualNodesFromNode(node);
  });

  return newOrgTree;
}

export const createAdminOrgTree = (user: UserData, orgTree: OrgTreeItem) => {
  const nodes: OrgTreeItem[] = [];
  if (user.PCRoles) {
    user.PCRoles.forEach((role) => {
      if (role.role !== "admin" && role.role !== "oversight") return;
      const correspondingNode = JSON.parse(
        JSON.stringify(findNodeByTeamId(orgTree, role.teamId))
      );
      nodes.push(correspondingNode);
    });
  }

  const filteredOrgTreeArray = filterOutDependentNodes(nodes);
  const noIndividualNodes = removeIndividualNodes(filteredOrgTreeArray);
  return noIndividualNodes as OrgTreeItem[];
};

// Function to check if a nodetwo is a equal/descendent of nodeOne and lower in power
function isDescendantOrEqual(
  nodeOne: OrgTreeItem,
  nodeTwo: OrgTreeItem
): boolean {
  if (
    nodeOne.teamId === nodeTwo.teamId &&
    (nodeOne.children?.length ?? 0) >= (nodeTwo.children?.length ?? 0)
  ) {
    return true;
  }

  if (nodeOne.children) {
    for (let child of nodeOne.children) {
      if (isDescendantOrEqual(child, nodeTwo)) return true;
    }
  }
  return false;
}

//function that checks an array of nodes and fliters out any node that is the descedant of another node in the array
export function filterOutDependentNodes(nodes: OrgTreeItem[]): OrgTreeItem[] {
  let independentNodes: OrgTreeItem[] = [...nodes];
  let indexesToDelete: number[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    for (let j = 0; j < nodes.length; j++) {
      if (
        i !== j &&
        !indexesToDelete.includes(i) &&
        !indexesToDelete.includes(j) &&
        isDescendantOrEqual(node, nodes[j])
      ) {
        indexesToDelete.push(j);
      }
    }
  }
  indexesToDelete.sort((a, b) => b - a);
  indexesToDelete.forEach((index) => {
    independentNodes.splice(index, 1);
  });
  return independentNodes;
}

export function getAllUserIdsFromOrgTreeNode(
  node: OrgTreeItem,
  orgTree: OrgTreeItem[]
): string[] {
  let teamIds: string[] = [];
  const matchingNode = findNodeByTeamId(orgTree[0], node.teamId);
  if (!matchingNode || !matchingNode.children) return [];
  matchingNode.children.forEach((childNode) => {
    if (
      childNode.teamType === "volunteer" ||
      childNode.teamType === "churchStaff" ||
      childNode.teamType === "pastoralCare" ||
      childNode.teamType === "oversight" ||
      childNode.teamType === "campusPastor" ||
      childNode.teamType === "admin"
    ) {
      teamIds.push(childNode.teamId);
    }
  });
  return teamIds;
}

export function getAllTeamIdsFromOrgTreeArray(
  orgTree: OrgTreeItem[],
  includeHiddenTeams: boolean = false
): string[] {
  let teamIds: string[] = [];
  const exportAllTeamIds = (node: OrgTreeItem) => {
    if (node.teamType === "campus" && includeHiddenTeams === true) {
      teamIds.push(node.teamId);
    }
    if (
      node.teamType === "campus" &&
      includeHiddenTeams === false &&
      node.visible === true
    ) {
      teamIds.push(node.teamId);
    }
    if (node.children) {
      node.children.forEach((child) => {
        exportAllTeamIds(child);
      });
    }
  };
  orgTree.forEach((node) => {
    exportAllTeamIds(node);
  });
  return teamIds;
}

export function getTeamLabel(teamId: string, OrgTree: OrgTreeItem) {
  const node = findNodeByTeamId(OrgTree, teamId);
  if (node) {
    return node.label;
  }
  return "";
}

export function iterateArrayOfNodesAndGiveParentsIfIndividualReporter(
  orgTreeItems: OrgTreeItem[],
  orgTree: OrgTreeItem[],
  user: UserData
): OrgTreeItem[] | undefined {
  const newArrayOfNodes: OrgTreeItem[] = [];
  orgTreeItems.forEach((node) => {
    if (
      (node.teamType === "volunteer" ||
        node.teamType === "churchStaff" ||
        node.teamType === "pastoralCare") &&
      node.key == node.teamId
    ) {
      const listOfParentsTeamIds: string[] = [];
      user.PCRoles?.forEach((role) => {
        if (
          role.role === "volunteer" ||
          role.role == "churchStaff" ||
          role.role == "pastoralCare"
        ) {
          listOfParentsTeamIds.push(role.teamId);
        }
      });
      listOfParentsTeamIds.forEach((teamId) => {
        const parentNode = findNodeById(orgTree, teamId);
        if (parentNode) {
          newArrayOfNodes.push(parentNode);
        }
      });
    } else if (
      (node.teamType === "volunteer" ||
        node.teamType === "churchStaff" ||
        node.teamType === "pastoralCare") &&
      node.key !== node.teamId
    ) {
      const parentKey = parentKeyFromChildKey(node.key);
      const parentNode = findNodeByKey(orgTree, parentKey);
      if (parentNode) {
        newArrayOfNodes.push(parentNode);
      }
    } else {
      newArrayOfNodes.push(node);
    }
  });
  const filteredOrgTreeArray = filterOutDependentNodes(newArrayOfNodes);
  return filteredOrgTreeArray;
}

/* {
  "teamType": "global",
  "visible": true,
  "children": [
      {
          "label": "Australia",
          "teamType": "country",
          "teamId": "A1B2C3D4E1",
          "visible": true,
          "children": [
              {
                  "visible": true,
                  "key": "0-0-0",
                  "label": "Campuses",
                  "teamId": "A1B2C3D4E2",
                  "teamType": "group",
                  "children": [
                      {
                          "visible": true,
                          "key": "0-0-0-0",
                          "teamId": "G1U2L3J5W5",
                          "teamType": "campus",
                          "label": "Brisbane - Greater Springfield"
                      },
                      {
                          "teamId": "E76J0V9M5U7",
                          "teamType": "campus",
                          "visible": false,
                          "key": "0-0-0-1",
                          "label": "Gold Coast - Coomera"
                      },
                      {
                          "key": "0-0-0-2",
                          "visible": false,
                          "teamType": "campus",
                          "teamId": "W58L6S0H0AZ",
                          "label": "Brisbane - West"
                      },
                      {
                          "teamId": "KK2L9JJ40EE",
                          "label": "Church Online",
                          "visible": false,
                          "teamType": "campus",
                          "key": "0-0-0-3"
                      },
                      {
                          "label": "Gold Coast - Burleigh",
                          "visible": false,
                          "teamType": "campus",
                          "key": "0-0-0-4",
                          "teamId": "CSX566H36K8R"
                      },
                      {
                          "key": "0-0-0-5",
                          "label": "Gold Coast - Coomera",
                          "teamType": "campus",
                          "visible": false,
                          "teamId": "T8XK076U36"
                      },
                      {
                          "teamId": "A8XK076U36",
                          "key": "0-0-0-6",
                          "visible": false,
                          "label": "Gold Coast - Surfers",
                          "teamType": "campus"
                      },
                      {
                          "visible": true,
                          "teamId": "AS56HH36K7V",
                          "key": "0-0-0-7",
                          "teamType": "campus",
                          "label": "Darwin - Palmerston"
                      },
                      {
                          "teamType": "group",
                          "label": "Campus Support 1",
                          "children": [
                              {
                                  "teamId": "A1B2C3D4E4",
                                  "key": "0-0-1-0-0",
                                  "teamType": "campus",
                                  "label": "Newcastle",
                                  "visible": true
                              },
                              {
                                  "key": "0-0-1-0-1",
                                  "teamId": "A1B2C3D4E5",
                                  "teamType": "campus",
                                  "label": "Sunshine Coast - North",
                                  "visible": true
                              },
                              {
                                  "label": "Sunshine Coast - South",
                                  "teamId": "A1B2C3D4E6",
                                  "key": "0-0-1-0-2",
                                  "visible": true,
                                  "teamType": "campus"
                              },
                              {
                                  "label": "Perth",
                                  "teamId": "A1B2C3D4E7",
                                  "key": "0-0-1-0-3",
                                  "visible": true,
                                  "teamType": "campus"
                              },
                              {
                                  "key": "0-0-1-0-4",
                                  "teamId": "A1B2C3D4E8",
                                  "visible": true,
                                  "teamType": "campus",
                                  "label": "Sydney - Greater West"
                              },
                              {
                                  "label": "Sydney - City",
                                  "visible": true,
                                  "teamType": "campus",
                                  "teamId": "A1B2C3D4E3",
                                  "key": "0-0-1-0-5"
                              },
                              {
                                  "key": "0-0-0-8-6",
                                  "teamId": "T3JDIGQYOP273FNWG5DNDR041TQ95H",
                                  "visible": false,
                                  "teamType": "group",
                                  "label": "Closed Campuses"
                              }
                          ],
                          "visible": true,
                          "teamId": "KPF303F093U0303345GNF03",
                          "key": "0-0-0-8"
                      },
                      {
                          "teamType": "group",
                          "visible": true,
                          "key": "0-0-0-9",
                          "children": [
                              {
                                  "visible": true,
                                  "teamId": "A1B2C3D4E9",
                                  "teamType": "campus",
                                  "key": "0-0-1-1-0",
                                  "label": "Cairns"
                              },
                              {
                                  "label": "Brisbane",
                                  "key": "0-0-1-1-1",
                                  "teamType": "campus",
                                  "visible": true,
                                  "teamId": "A1B2C3D4F0"
                              },
                              {
                                  "teamType": "campus",
                                  "visible": true,
                                  "label": "Gold Coast North",
                                  "key": "0-0-1-1-2",
                                  "teamId": "A1B2C3D4F1"
                              },
                              {
                                  "visible": true,
                                  "teamId": "A1B2C3D4F2",
                                  "label": "Sydney - Merrylands",
                                  "teamType": "campus",
                                  "key": "0-0-1-1-3"
                              },
                              {
                                  "key": "0-0-1-1-4",
                                  "label": "Sydney - Inner West",
                                  "teamId": "A1B2C3D4F3",
                                  "visible": true,
                                  "teamType": "campus"
                              },
                              {
                                  "teamId": "A1B2C3D4F4",
                                  "key": "0-0-1-1-5",
                                  "label": "Sydney - North Shore",
                                  "visible": true,
                                  "teamType": "campus"
                              },
                              {
                                  "visible": true,
                                  "label": "Wollongong",
                                  "key": "0-0-1-1-6",
                                  "teamType": "campus",
                                  "teamId": "A1B2C3D4F5"
                              },
                              {
                                  "teamType": "campus",
                                  "key": "0-0-1-1-7",
                                  "teamId": "A1B2C3D4F6",
                                  "label": "Sydney - Northern Beaches",
                                  "visible": true
                              },
                              {
                                  "teamId": "A1B2C3D4F7",
                                  "key": "0-0-1-1-8",
                                  "label": "Bondi",
                                  "teamType": "campus",
                                  "visible": true
                              },
                              {
                                  "teamType": "campus",
                                  "key": "0-0-1-1-9",
                                  "teamId": "A1B2C3D4F8",
                                  "label": "Geelong",
                                  "visible": true
                              }
                          ],
                          "teamId": "2290FH3085PWPSMNUVC752",
                          "label": "Campus Support 2"
                      },
                      {
                          "visible": true,
                          "teamType": "group",
                          "label": "Campus Support 3",
                          "children": [
                              {
                                  "teamId": "A1B2C3D4F9",
                                  "teamType": "campus",
                                  "key": "0-0-1-2-0",
                                  "visible": true,
                                  "label": "Sydney - Hills"
                              },
                              {
                                  "visible": true,
                                  "teamId": "A1B2C3D5E0",
                                  "teamType": "campus",
                                  "key": "0-0-1-2-1",
                                  "label": "Brisbane - Central"
                              },
                              {
                                  "visible": true,
                                  "teamId": "A1B2C3D5E1",
                                  "teamType": "campus",
                                  "key": "0-0-1-2-2",
                                  "label": "Gold Coast - Central"
                              },
                              {
                                  "teamType": "campus",
                                  "visible": true,
                                  "teamId": "A1B2C3D5E2",
                                  "label": "Melbourne - City",
                                  "key": "0-0-1-2-3"
                              },
                              {
                                  "visible": true,
                                  "label": "Melbourne - East",
                                  "teamId": "A1B2C3D5E3",
                                  "key": "0-0-1-2-4",
                                  "teamType": "campus"
                              },
                              {
                                  "label": "Sydney - Villawood",
                                  "visible": true,
                                  "teamType": "campus",
                                  "key": "0-0-1-2-5",
                                  "teamId": "A1B2C3D5E4"
                              }
                          ],
                          "key": "0-0-0-10",
                          "teamId": "282048HF049IESINFCU30EW"
                      },
                      {
                          "children": [
                              {
                                  "visible": true,
                                  "key": "0-0-1-3-0",
                                  "label": "Sydney - Macquarie",
                                  "teamId": "A1B2C3D5E5",
                                  "teamType": "campus"
                              },
                              {
                                  "label": "Brisbane - Downtown",
                                  "visible": true,
                                  "teamId": "A1B2C3D5E6",
                                  "teamType": "campus",
                                  "key": "0-0-1-3-1"
                              },
                              {
                                  "visible": true,
                                  "label": "Darwin",
                                  "teamId": "A1B2C3D5E7",
                                  "teamType": "campus",
                                  "key": "0-0-1-3-2"
                              },
                              {
                                  "label": "Melbourne - West",
                                  "teamId": "A1B2C3D5E8",
                                  "teamType": "campus",
                                  "visible": true,
                                  "key": "0-0-1-3-3"
                              },
                              {
                                  "label": "Melbourne - Greater West",
                                  "teamType": "campus",
                                  "key": "0-0-1-3-4",
                                  "visible": true,
                                  "teamId": "A1B2C3D5E9"
                              },
                              {
                                  "teamId": "A1B2C3D5F0",
                                  "visible": true,
                                  "label": "Hobart",
                                  "teamType": "campus",
                                  "key": "0-0-1-3-5"
                              },
                              {
                                  "key": "0-0-1-3-6",
                                  "label": "Launceston",
                                  "teamId": "A1B2C3D5F1",
                                  "teamType": "campus",
                                  "visible": true
                              },
                              {
                                  "teamId": "A1B2C3D5F2",
                                  "key": "0-0-1-3-7",
                                  "visible": true,
                                  "teamType": "campus",
                                  "label": "Sydney - South West"
                              }
                          ],
                          "teamId": "293485HGFDODIFGTUHFNHGFE",
                          "key": "0-0-0-11",
                          "visible": true,
                          "teamType": "group",
                          "label": "Campus Support 4"
                      }
                  ]
              },
              {
                  "teamId": "L3PAYDJ2W1",
                  "key": "0-0-1",
                  "visible": true,
                  "label": "National",
                  "teamType": "campus"
              }
          ],
          "key": "0-0"
      },
      {
          "teamId": "qPZGUYYqrOiAmdUOlEc6",
          "teamType": "campus",
          "key": "0-1",
          "label": "Testing Team",
          "visible": true
      },
      {
          "children": [],
          "label": "Bali",
          "teamType": "campus",
          "key": "0-2",
          "visible": false,
          "teamId": "WTK2H3T9O2"
      }
  ],
  "label": "Global",
  "key": "0",
  "teamId": "A1B2C3D4E0"
} */

export function addAllUsersToOrgTree(
  users: UserData[],
  orgTree: OrgTreeItem[]
) {
  const newOrgTree = [...orgTree];
  users.forEach((user) => {
    user.PCRoles?.forEach((role) => {
      const node = findNodeByTeamId(newOrgTree[0], role.teamId);
      if (node) {
        !node.children && (node.children = []);
        node.children.push({
          key: `${node.key}-${node.children.length}`,
          label: `${user.firstName} ${user.lastName}`,
          teamId: user.userId,
          teamType: role.role,
          visible: true,
        });
      }
    });
  });
  return newOrgTree;
}
//update orgTree with new user roles
export function updateOrgTreeWithNewUserRoles(
  orgTree: OrgTreeItem,
  user: UserData
) {
  const newOrgTree: OrgTreeItem = JSON.parse(JSON.stringify(orgTree));

  const deleteAllUserNodes = (node: OrgTreeItem) => {
    if (node.children) {
      node.children = node.children.filter(
        (child) => child.teamId !== user.userId
      );
      node.children.forEach((child) => {
        deleteAllUserNodes(child);
      });
    }
  };
  deleteAllUserNodes(newOrgTree);

  user.PCRoles.forEach((role) => {
    const node = findNodeByTeamId(newOrgTree, role.teamId);
    if (node) {
      if (node.children) {
        node.children.push({
          key: `${node.key}-${node.children.length}`,
          label: `${user.firstName} ${user.lastName}`,
          teamId: user.userId,
          teamType: role.role,
          visible: true,
        });
      } else {
        node.children = [];
        node.children.push({
          key: `${node.key}-${node.children.length}`,
          label: `${user.firstName} ${user.lastName}`,
          teamId: user.userId,
          teamType: role.role,
          visible: true,
        });
      }
    }
  });

  return newOrgTree;
}